import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close,
  Description,
  PictureAsPdf,
  PlayCircleOutline,
} from '@mui/icons-material';

import {
  CatalogEntry,
  CircuitDay,
  CircuitOptional,
  CircuitProduct,
  useCircuitProduct,
} from '../queries';
import { catalogMeta, itineraryCityChain } from '../circuits';
import { circuitPrintModel, clientPdfGate } from '../circuitPrint';
import { CircuitPrintDialog } from './CircuitPrintDialog';
import { CircuitItineraryChain } from './CircuitItineraryChain';
import { formatMoney } from '../money';
import { formatMinutes } from '../format';
import { useAudience } from '../audience';
import { CONFIG } from '@frontend/config';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useGetAgencyById } from '@frontend/features/agencies/queries';
import { MetaLine } from './MetaLine';

interface Props {
  productRef?: string;
  /**
   * Proveedor del producto. El ref es PROVIDER-SCOPED (contrato del hub), así
   * que sin él la ficha podría traer —o servir del cache— el producto de otro
   * proveedor con el mismo ref.
   */
  provider?: string;
  /**
   * La entrada del catálogo que abrió la ficha. Es un SNAPSHOT (21.23): aporta
   * el "desde" indicativo —que la ficha no trae: el contrato del producto no
   * tiene precio— y es lo que se manda a cotizar al avanzar. Ausente cuando la
   * ficha se abre desde el encabezado de salidas: ahí el precio real ya está
   * en pantalla y no hay a dónde avanzar.
   */
  entry?: CatalogEntry;
  /**
   * Proveedor del que la lista de conectores dice que NO cotiza salidas en
   * esta agencia. Es un AVISO, no un gate: esa lista se cachea (10 min en el
   * backend, 30 min en el cliente) y puede no haber visto credenciales
   * recién cargadas, así que el paso a las salidas sigue disponible y el hub
   * —la autoridad— decide. Lo que cambia es que el asesor lee el motivo
   * antes del click, en vez de recibir un "probá otra ventana de fechas" que
   * sería falso.
   */
  unsearchableProvider?: string;
  onSeeDepartures?: (entry: CatalogEntry) => void;
  onClose: () => void;
}

/** Días que se ven sin expandir: el itinerario puede tener quince. */
const ITINERARY_PREVIEW = 2;

/** Ancho del panel de venta. El mismo número que el wireframe de criterio. */
const SIDE_PANEL_WIDTH = 296;

/**
 * Rótulo de sección del panel: mayúsculas chicas con tracking. Es el separador
 * de bloques de toda la ficha, así que vive en un solo lugar.
 */
function SideLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      component="p"
      sx={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'text.secondary',
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Link al material del operador (video, folleto). La URL ya viene validada por
 * el backend (21.11), acá solo se pinta. `rel="noreferrer"` porque es un
 * dominio de terceros.
 */
function ProviderLink({
  url,
  icon,
  children,
}: {
  url: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={url}
      target="_blank"
      rel="noreferrer"
      underline="hover"
      sx={{
        fontSize: 12,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {icon}
      {children}
    </Link>
  );
}

/** Líneas que se leen sin expandir. Compacto por defecto, nunca definitivo. */
const CLAMP_LINES = 2;

/**
 * Texto del proveedor recortado que se puede abrir COMPLETO. El recorte se
 * MIDE (`scrollHeight` contra `clientHeight`) en vez de adivinarse con un largo
 * de corte: el contrato permite `description` sin `detail`, y un relato de
 * cinco líneas sin forma de abrirlo es material de venta inaccesible.
 *
 * La medición ocurre solo mientras está recortado y `overflows` no vuelve a
 * `false`: si se midiera expandido, el link para volver a cerrarlo
 * desaparecería.
 */
function useExpandableText(collapsed?: string, expanded?: string) {
  const ref = useRef<HTMLElement | null>(null);
  const [full, setFull] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const text = full ? expanded ?? collapsed : collapsed;

  useEffect(() => {
    const node = ref.current;
    if (!node || full) return;
    setOverflows(node.scrollHeight - node.clientHeight > 1);
  }, [text, full]);

  // Al cambiar de texto se vuelve a recortar; `overflows` no se resetea acá: lo
  // recalcula la medición de arriba (y hacerlo acá la pisaba en el montaje,
  // porque los efectos corren en orden de declaración).
  useEffect(() => {
    setFull(false);
  }, [collapsed, expanded]);

  return {
    ref,
    text,
    full,
    toggle: () => setFull((prev) => !prev),
    // Hay más para leer si el proveedor publica una versión larga aparte o si
    // lo que se muestra no entra en las dos líneas.
    hasMore: Boolean(expanded && expanded !== collapsed) || overflows,
    clampSx: full
      ? {}
      : {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: CLAMP_LINES,
        overflow: 'hidden',
      },
  };
}

/**
 * Un día del itinerario: el badge `D01`, la ciudad y el relato del operador
 * recortado a dos líneas. La versión larga (`detail`) se abre a pedido y solo
 * existe cuando el proveedor publica las dos — nunca un link que muestre lo
 * mismo que ya se está leyendo.
 */
function ItineraryDayRow({ day }: { day: CircuitDay }) {
  const { t } = useTranslation();
  const story = useExpandableText(day.description ?? day.detail, day.detail);

  return (
    <Box
      display="grid"
      gridTemplateColumns="42px minmax(0, 1fr)"
      gap={1.5}
      py={1.25}
      sx={{ '& + &': { borderTop: 1, borderColor: 'divider' } }}
    >
      <Chip
        size="small"
        label={`D${String(day.day).padStart(2, '0')}`}
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      />
      <Box minWidth={0}>
        {day.title && (
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.35 }}>
            {day.title}
          </Typography>
        )}
        {story.text && (
          <Typography
            ref={story.ref}
            color="text.secondary"
            sx={{
              fontSize: 12.5,
              lineHeight: 1.45,
              whiteSpace: 'pre-line',
              // Sin expandir, dos líneas: el relato de un día son cuatro mil
              // caracteres y el itinerario tiene quince.
              ...story.clampSx,
            }}
          >
            {story.text}
          </Typography>
        )}
        {(story.hasMore || day.videoUrl) && (
          <Stack direction="row" spacing={1.25} mt={0.5} flexWrap="wrap" useFlexGap>
            {story.hasMore && (
              <Link
                component="button"
                type="button"
                underline="hover"
                sx={{ fontSize: 12 }}
                onClick={story.toggle}
              >
                {story.full
                  ? t('travelServices.circuits.dayLess')
                  : t('travelServices.circuits.dayFull')}
              </Link>
            )}
            {day.videoUrl && (
              <ProviderLink
                url={day.videoUrl}
                icon={<PlayCircleOutline sx={{ fontSize: 15 }} />}
              >
                {t('travelServices.circuits.dayVideo')}
              </ProviderLink>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/**
 * Una excursión opcional. El precio va SIEMPRE en su propio slot a la derecha,
 * rotulado como precio del proveedor: cuando haya markup de adicionales, el
 * importe markupeado ocupa ese mismo lugar sin mover el layout.
 */
function OptionalRow({
  optional,
  selected,
  onToggle,
}: {
  optional: CircuitOptional;
  selected: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const duration = formatMinutes(optional.durationMinutes) ?? optional.duration;
  const story = useExpandableText(optional.description);

  return (
    <Box
      display="grid"
      gridTemplateColumns="28px minmax(0, 1fr) 120px"
      gap={1.5}
      py={1}
      sx={{
        '& + &': { borderTop: 1, borderColor: 'divider' },
        // La elegida se lee de un vistazo: es lo que va a salir destacado en
        // el PDF que el asesor le manda al cliente.
        ...(selected
          ? {
            bgcolor: 'action.hover',
            borderLeft: 2,
            borderColor: 'primary.main',
            pl: 1,
            ml: -1.25,
          }
          : {}),
      }}
    >
      <Checkbox
        size="small"
        checked={selected}
        onChange={onToggle}
        inputProps={{ 'aria-label': optional.name }}
        sx={{ p: 0.25, alignSelf: 'start', mt: -0.25 }}
      />
      <Box minWidth={0}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>
          {optional.name}
        </Typography>
        <MetaLine items={[optional.city, duration]} />
        {story.text && (
          <Typography
            ref={story.ref}
            color="text.secondary"
            sx={{
              fontSize: 11.5,
              lineHeight: 1.4,
              mt: 0.25,
              whiteSpace: 'pre-line',
              ...story.clampSx,
            }}
          >
            {story.text}
          </Typography>
        )}
        {story.hasMore && (
          <Link
            component="button"
            type="button"
            underline="hover"
            sx={{ fontSize: 11.5, mt: 0.25 }}
            onClick={story.toggle}
          >
            {story.full
              ? t('travelServices.circuits.dayLess')
              : t('travelServices.circuits.optionalFull')}
          </Link>
        )}
      </Box>
      <Box textAlign="right">
        {optional.videoUrl && (
          <Box mb={0.25}>
            <ProviderLink
              url={optional.videoUrl}
              icon={<PlayCircleOutline sx={{ fontSize: 15 }} />}
            >
              {t('travelServices.circuits.video')}
            </ProviderLink>
          </Box>
        )}
        {optional.price ? (
          <>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMoney(optional.price)}
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: 10.5 }}>
              {t('travelServices.circuits.providerPrice')}
            </Typography>
          </>
        ) : (
          // El proveedor publica opcionales sin precio: se ofrecen igual, el
          // importe se confirma con el operador.
          <Typography color="text.secondary" sx={{ fontSize: 10.5 }}>
            {t('travelServices.circuits.optionalNoPrice')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Los sellos del producto. Solo se pinta el que el contrato trae. */
function ProductFlags({ product }: { product: CircuitProduct }) {
  const { t } = useTranslation();
  const flags = [
    product.guaranteedDeparture && {
      key: 'guaranteed',
      color: 'success' as const,
      label: t('travelServices.circuits.guaranteedDeparture'),
    },
    // Solo el caso que CAMBIA la operación: on-request. "Confirma al instante"
    // es lo esperable y como chip verde competía con la salida garantizada.
    product.immediateConfirmation === false && {
      key: 'onRequest',
      color: 'warning' as const,
      label: t('travelServices.circuits.toConfirmByOperator'),
    },
    product.passportRequired && {
      key: 'passport',
      color: 'info' as const,
      label: t('travelServices.circuits.passportFlag'),
    },
  ].filter(Boolean) as { key: string; color: 'success' | 'warning' | 'info'; label: string }[];

  if (flags.length === 0) return null;

  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {flags.map((flag) => (
        <Chip
          key={flag.key}
          size="small"
          color={flag.color}
          variant="outlined"
          label={flag.label}
          sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
        />
      ))}
    </Stack>
  );
}

/**
 * Ficha de un circuito: la etapa de VENTA, entre el catálogo (qué circuitos
 * existen) y las salidas (cuándo y a cuánto). Itinerario día a día a la
 * izquierda, y a la derecha el panel que cierra: mapa del recorrido, qué cubre
 * el precio, el material del operador y el paso a las salidas.
 *
 * TODAS las secciones son opcionales y varían por producto (hay circuitos con
 * trece días de itinerario y ningún folleto, y otros al revés), así que cada
 * bloque se pinta solo si tiene contenido — nunca un título con el vacío
 * abajo. Y nada que el contrato no traiga: no hay hoteles ni régimen de
 * comidas por día porque el hub no los publica.
 */
export function CircuitProductDialog({
  productRef,
  provider,
  entry,
  unsearchableProvider,
  onSeeDepartures,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const { isAgent } = useAudience();
  const {
    data: product,
    isLoading,
    isError,
  } = useCircuitProduct(productRef, provider);
  const [imageFailed, setImageFailed] = useState(false);
  const [allDays, setAllDays] = useState(false);
  /** Las excursiones que el asesor marcó para el PDF del cliente. */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [printing, setPrinting] = useState(false);

  // El diálogo vive montado (se abre por `productRef`), así que su estado de
  // lectura es del PRODUCTO, no del componente: sin este reseteo, un mapa que
  // no cargó dejaba sin mapa al circuito siguiente y el itinerario se abría ya
  // expandido en un producto que el asesor recién abre.
  useEffect(() => {
    setImageFailed(false);
    setAllDays(false);
    setSelected(new Set());
    setPrinting(false);
  }, [productRef, provider]);

  const toggleOptional = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // La marca del documento es la de la AGENCIA seleccionada (la misma contra la
  // que se busca), y quien cotiza es el asesor logueado: nada hardcodeado.
  const { data: agency, isFetching: agencyFetching } = useGetAgencyById({
    id: CONFIG.AGENCY_ID,
  });
  const { myProfile } = useAuth();
  const advisor = useMemo(() => {
    if (!myProfile) return undefined;
    const name = [myProfile.firstName, myProfile.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    const phone = [myProfile.phoneCountryCode, myProfile.phoneNumber]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name
      ? { name, contact: [myProfile.email, phone].filter(Boolean) as string[] }
      : undefined;
  }, [myProfile]);

  const meta = entry ? catalogMeta(entry) : undefined;
  // El mapa del recorrido. La ficha del proveedor no siempre publica `images`
  // aunque su entrada del catálogo sí (pasa con todo Europamundo hoy): es el
  // MISMO mapa y el mismo campo, ya saneado, así que la entrada que abrió la
  // ficha sirve de respaldo antes que dejar el panel sin su argumento de venta.
  const map = imageFailed
    ? undefined
    : product?.images?.[0] ?? meta?.images?.[0];
  const itinerary = product?.itinerary ?? [];
  const chain = itineraryCityChain(itinerary);
  const days = allDays ? itinerary : itinerary.slice(0, ITINERARY_PREVIEW);
  const pricing = meta?.pricing;
  const brochures = product?.brochureUrls ?? [];

  /**
   * Lo que se va a imprimir. Se arma al abrir el documento —no en cada
   * render— para que la fecha de generación sea UNA y quede congelada con el
   * papel que el asesor está mirando.
   */
  const pdf = clientPdfGate({
    agencyName: agency?.name,
    isFetching: agencyFetching,
  });

  const printModel = useMemo(
    () =>
      printing && product && agency?.name
        ? circuitPrintModel({
          product,
          entry,
          selected,
          agencyName: agency.name,
          advisor,
          // Día y mes de dos dígitos: la fecha va impresa en un documento
          // que se lee meses después, no en una pantalla.
          generatedAt: new Date().toLocaleDateString(i18n.language, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
          optionalDuration: (optional) =>
            formatMinutes(optional.durationMinutes) ?? optional.duration,
        })
        : undefined,
    [printing, product, entry, selected, agency?.name, advisor, i18n.language],
  );

  return (
    <Dialog open={Boolean(productRef)} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="flex-start" gap={1}>
          <Box flex={1} minWidth={0}>
            {/* El proveedor es PROCEDENCIA, no argumento de venta: chico, gris
                y solo para el asesor, igual que en la card del catálogo. */}
            {isAgent && product?.provider && (
              <Typography component="p" color="text.disabled" sx={{ fontSize: 11.5 }}>
                {product.provider}
              </Typography>
            )}
            <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.3 }}>
              {product?.name ?? entry?.name ?? t('travelServices.circuits.productSheet')}
            </Typography>
            {chain.stops.length > 0 && (
              <Box mt={0.5}>
                <CircuitItineraryChain
                  stops={chain.stops}
                  withDays={chain.withDays}
                  tail={[
                    chain.truncated ? '…' : undefined,
                    product?.durationDays
                      ? t('travelServices.durationDays', { count: product.durationDays })
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              </Box>
            )}
          </Box>
          {product && <ProductFlags product={product} />}
          <Button size="small" onClick={onClose} startIcon={<Close />}>
            {t('travelServices.booking.close')}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {isError && (
          <Alert severity="error">{t('travelServices.circuits.sheetError')}</Alert>
        )}

        {product && (
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: '1fr',
              md: `minmax(0, 1fr) ${SIDE_PANEL_WIDTH}px`,
            }}
            gap={2.25}
          >
            <Box minWidth={0}>
              {product.description && (
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: 12.5, lineHeight: 1.5, mb: 1.5, whiteSpace: 'pre-line' }}
                >
                  {product.description}
                </Typography>
              )}

              {itinerary.length > 0 && (
                <Box mb={2}>
                  <SideLabel>{t('travelServices.circuits.itineraryByDay')}</SideLabel>
                  {days.map((day, index) => (
                    // La key lleva el producto: el "ver día completo" de cada
                    // fila es estado del DÍA de ESTE circuito, y sin el ref una
                    // fila abierta quedaba abierta en el circuito siguiente.
                    <ItineraryDayRow
                      key={`${provider}-${productRef}-${day.day}-${index}`}
                      day={day}
                    />
                  ))}
                  {itinerary.length > ITINERARY_PREVIEW && (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      sx={{ fontSize: 12.5, mt: 1 }}
                      onClick={() => setAllDays((prev) => !prev)}
                    >
                      {allDays
                        ? t('travelServices.circuits.collapseDays')
                        : t('travelServices.circuits.seeAllDays', {
                          count: itinerary.length,
                        })}
                    </Link>
                  )}
                </Box>
              )}

              {product.optionals?.length ? (
                <Box>
                  <SideLabel>{t('travelServices.circuits.optionalsAside')}</SideLabel>
                  {product.optionals.map((optional, index) => (
                    <OptionalRow
                      key={`${optional.id}-${index}`}
                      optional={optional}
                      selected={selected.has(optional.id)}
                      onToggle={() => toggleOptional(optional.id)}
                    />
                  ))}
                  {selected.size > 0 && (
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: 11.5, mt: 0.75 }}
                    >
                      {t('travelServices.circuits.selectedCount', {
                        count: selected.size,
                      })}
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Box>

            <Box minWidth={0}>
              {/* El mapa del recorrido que publica el proveedor. No hay
                  coordenadas en el contrato: un mapa interactivo sería inventar
                  datos. Si no carga, la caja desaparece — un recuadro vacío
                  donde va el argumento de venta es peor que no tenerlo. */}
              {map && (
                <Box
                  mb={1.5}
                  borderRadius={1}
                  border={1}
                  borderColor="divider"
                  overflow="hidden"
                  bgcolor="grey.100"
                >
                  <Box
                    component="img"
                    src={map}
                    alt=""
                    loading="lazy"
                    sx={{ display: 'block', width: '100%', height: 'auto' }}
                    onError={() => setImageFailed(true)}
                  />
                </Box>
              )}

              {product.includes?.length ? (
                <Box mb={1.5}>
                  <SideLabel>{t('travelServices.circuits.includes')}</SideLabel>
                  <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                    {product.includes.map((item, index) => (
                      <Typography
                        key={index}
                        component="li"
                        color="text.secondary"
                        sx={{
                          fontSize: 12.5,
                          lineHeight: 1.45,
                          py: 0.375,
                          pl: 2.5,
                          position: 'relative',
                          whiteSpace: 'pre-line',
                          '&::before': {
                            content: '"✓"',
                            position: 'absolute',
                            left: 2,
                            color: 'success.main',
                            fontWeight: 700,
                            fontSize: 11,
                          },
                        }}
                      >
                        {item}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ) : null}

              {/* El papel para el cliente. Va antes que el material del
                  operador: es el que lleva la marca de la agencia y la
                  curaduría del asesor; el folleto del proveedor viene con la
                  marca del proveedor y sin nuestro control. */}
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<Description />}
                disabled={!pdf.enabled}
                onClick={() => setPrinting(true)}
                sx={{ mb: pdf.reason ? 0.5 : 1.5 }}
              >
                {t('travelServices.circuits.clientPdf')}
              </Button>
              {/* Por qué no se puede generar todavía. Un botón apagado sin
                  motivo se lee como un bug del sistema. */}
              {pdf.reason && (
                <Typography
                  color={pdf.reason === 'loading' ? 'text.secondary' : 'warning.main'}
                  sx={{ fontSize: 11, mb: 1.5, display: 'block' }}
                >
                  {t(`travelServices.circuits.print.${pdf.reason}`)}
                </Typography>
              )}

              {(brochures.length > 0 || product.videoUrl) && (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
                  {product.videoUrl && (
                    <ProviderLink
                      url={product.videoUrl}
                      icon={<PlayCircleOutline sx={{ fontSize: 15 }} />}
                    >
                      {t('travelServices.circuits.video')}
                    </ProviderLink>
                  )}
                  {brochures.map((url, index) => (
                    <ProviderLink
                      key={url}
                      url={url}
                      icon={<PictureAsPdf sx={{ fontSize: 15 }} />}
                    >
                      {brochures.length > 1
                        ? t('travelServices.circuits.brochure', { number: index + 1 })
                        : t('travelServices.circuits.operatorBrochure')}
                    </ProviderLink>
                  ))}
                </Stack>
              )}

              {/* El paso a las salidas. Va con la entrada del catálogo que
                  abrió la ficha, no con el estado actual del formulario
                  (21.23): el asesor reserva lo que está leyendo. */}
              {entry && onSeeDepartures && (
                <Box>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => onSeeDepartures(entry)}
                  >
                    {t('travelServices.circuits.seeDepartures')}
                  </Button>
                  {unsearchableProvider && (
                    <Typography
                      color="warning.dark"
                      align="center"
                      sx={{ fontSize: 11.5, mt: 0.75 }}
                    >
                      {t('travelServices.circuits.providerNotSearchable', {
                        provider: unsearchableProvider,
                      })}
                    </Typography>
                  )}
                  {pricing && (
                    <Typography
                      color="text.secondary"
                      align="center"
                      sx={{ fontSize: 11, mt: 0.75 }}
                    >
                      {t('travelServices.circuits.fromPerPersonIndicative', {
                        price: formatMoney(pricing.final),
                      })}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      <CircuitPrintDialog
        model={printModel}
        onClose={() => setPrinting(false)}
      />
    </Dialog>
  );
}
